import { validateEnv } from '../../utils/env.util';

// Summary: Generic SSE subscription primitive shared by every entity-events service (areas, pos, employees, products, tenants, users, prin...
// Details: docs/function-library/web/services.md#sse-subscribe-util
export interface SubscribeStreamOptions<TEvent> {
  // Mints a single-use ticket via authenticated POST. Resolves to the ticket string.
  mintTicket: () => Promise<string>;
  // Builds the stream URL given the ticket and optional lastEventId. Must include the ticket in the query string. lastEventId is null on the first connect and on every reconnect that has not yet received any event with an id.
  buildStreamUrl: (ticket: string, lastEventId: string | null) => string;
  // Validates and returns a typed event from the raw parsed JSON payload, or null/undefined to drop.
  parseEvent: (payload: unknown) => TEvent | null | undefined;
  // Handler invoked for every accepted event.
  onEvent: (event: TEvent) => void;
  // Optional handler invoked when the local event reducer throws. Used by callers to recover stale local state.
  onEventError?: (error: unknown) => void;
  // Optional handler invoked once per failed connection attempt.
  onError?: () => void;
  // Optional handler invoked every time the EventSource (re)opens; the liveness signal that clears
  // the offline banner even on an idle stream where no domain event follows the reconnect.
  onOpen?: () => void;
  // Optional handler invoked when the server emits sse.resync (replay buffer capped). Caller should do a full GET reload of the affected slice.
  onResync?: () => void;
  // Optional handler invoked once after an established stream needs recovery following a sleep, hidden-tab, or offline wake.
  onRecovery?: (inactiveMs?: number) => void;
}

// Summary: Default URL builder appends ?ticket=...
// Details: docs/function-library/web/services.md#sse-subscribe-util
export function defaultStreamUrl(path: string, ticket: string, lastEventId: string | null = null): string {
  const { VITE_BACKEND_LOCATION } = validateEnv();
  const streamUrl = new URL(path, VITE_BACKEND_LOCATION);
  streamUrl.searchParams.set('ticket', ticket);
  if (lastEventId) {
    streamUrl.searchParams.set('lastEventId', lastEventId);
  }
  return streamUrl.toString();
}

// Summary: Opens a managed SSE subscription with single-use-ticket reconnect semantics and Last-Event-ID replay.
// Details: docs/function-library/web/services.md#sse-subscribe-util
export function subscribeStream<TEvent>(options: SubscribeStreamOptions<TEvent>): () => void {
  const { mintTicket, buildStreamUrl, parseEvent, onEvent, onEventError, onError, onOpen, onResync, onRecovery } = options;
  let source: EventSource | null = null;
  let cancelled = false;
  let isConnecting = false;
  let reconnectOnVisible = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = 1_000;
  let lastEventId: string | null = null;
  let recoveryRequested = false;

  const HEARTBEAT_MS = 4_000;
  const SLEEP_THRESHOLD_MS = 8_000;
  let lastTickAt = Date.now();
  let heartbeat: ReturnType<typeof setInterval> | null = null;

  const isDocumentHidden = () =>
    typeof document !== 'undefined' && document.visibilityState === 'hidden';

  const requestRecovery = (inactiveMs: number) => {
    // A baseline confirms this is a recovered established stream, not a focus event during initial load.
    if (recoveryRequested || lastEventId === null) {
      return;
    }
    recoveryRequested = true;
    onRecovery?.(Math.max(0, inactiveMs));
  };

  const scheduleReconnect = () => {
    if (cancelled || reconnectTimer) {
      return;
    }
    if (isDocumentHidden()) {
      reconnectOnVisible = true;
      return;
    }
    const delay = backoffMs;
    backoffMs = Math.min(backoffMs * 2, 30_000);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, delay);
  };

  const forceReconnect = () => {
    if (cancelled) {
      return;
    }
    if (isConnecting) {
      return;
    }
    if (source) {
      source.close();
      source = null;
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    backoffMs = 1_000;
    connect();
  };

  // Summary: 4s wall-clock heartbeat.
  // Details: docs/function-library/web/services.md#sse-subscribe-util
  const onHeartbeat = () => {
    if (cancelled) {
      return;
    }
    if (isDocumentHidden()) {
      return;
    }
    const now = Date.now();
    const gap = now - lastTickAt;
    lastTickAt = now;
    if (gap > SLEEP_THRESHOLD_MS) {
      requestRecovery(gap);
      forceReconnect();
    }
  };

  // Summary: Belt-and-suspenders wake signal for environments where the heartbeat is delayed (mobile pageshow, network coming back online, t...
  // Details: docs/function-library/web/services.md#sse-subscribe-util
  const onWakeSignal = () => {
    if (cancelled) {
      return;
    }
    if (isDocumentHidden()) {
      return;
    }
    const now = Date.now();
    const gap = now - lastTickAt;
    lastTickAt = now;
    if (reconnectOnVisible) {
      reconnectOnVisible = false;
      requestRecovery(gap);
      forceReconnect();
      return;
    }
    if (gap > SLEEP_THRESHOLD_MS) {
      requestRecovery(gap);
      forceReconnect();
      return;
    }
    const ES = typeof EventSource !== 'undefined' ? EventSource : null;
    if (source && ES && source.readyState === ES.OPEN) {
      return;
    }
    requestRecovery(gap);
    forceReconnect();
  };

  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pageshow', onWakeSignal);
    window.addEventListener('focus', onWakeSignal);
    window.addEventListener('online', onWakeSignal);
  }
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', onWakeSignal);
  }
  heartbeat = setInterval(onHeartbeat, HEARTBEAT_MS);

  const connect = () => {
    if (cancelled || isConnecting) {
      return;
    }
    if (isDocumentHidden()) {
      reconnectOnVisible = true;
      return;
    }
    isConnecting = true;
    void mintTicket()
      .then((ticket) => {
        if (cancelled) {
          return;
        }
        const next = new EventSource(buildStreamUrl(ticket, lastEventId), { withCredentials: true });
        source = next;

        next.onopen = () => {
          backoffMs = 1_000;
          // Re-arm so every later sleep/wake cycle can trigger recovery again, not just the first.
          recoveryRequested = false;
          onOpen?.();
        };
        next.onmessage = (message) => {
          if (message.lastEventId) {
            lastEventId = message.lastEventId;
          }
          try {
            const parsed = JSON.parse(message.data) as unknown;
            if (parsed && typeof parsed === 'object') {
              const type = (parsed as { type?: unknown }).type;
              if (type === 'sse.head') {
                // Synthetic baseline frame; only its id matters (already captured above).
                return;
              }
              if (type === 'sse.resync') {
                if (onResync) {
                  onResync();
                }
                return;
              }
            }
            const event = parseEvent(parsed);
            if (event) {
              try {
                onEvent(event);
              } catch (eventError) {
                onEventError?.(eventError);
              }
            }
          } catch {
            // Ignore malformed frames and keep the connection alive.
          }
        };
        next.onerror = () => {
          if (cancelled) {
            return;
          }
          next.close();
          if (source === next) {
            source = null;
          }
          if (onError) {
            onError();
          }
          scheduleReconnect();
        };
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        if (onError) {
          onError();
        }
        scheduleReconnect();
      })
      .finally(() => {
        isConnecting = false;
      });
  };

  connect();

  return () => {
    cancelled = true;
    if (heartbeat) {
      clearInterval(heartbeat);
      heartbeat = null;
    }
    reconnectOnVisible = false;
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('pageshow', onWakeSignal);
      window.removeEventListener('focus', onWakeSignal);
      window.removeEventListener('online', onWakeSignal);
    }
    if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      document.removeEventListener('visibilitychange', onWakeSignal);
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (source) {
      source.close();
      source = null;
    }
  };
}


